import { parseBookingRequirements } from '../components/VenueDashboard/utils/bookingAddOns';
import { isVenueBookingListSummary } from '../components/VenueDashboard/utils/venueBookingListModel';
import {
  computePaymentFeeBreakdown,
  extractFeeBreakdownSnapshot,
  isManualBookingSource
} from './bookingPaymentFees';
import {
  computeBaseFromPricingMetadata,
  readPricingMetadata
} from './bookingPricingMetadata';
import {
  FREE_BOOKING_MODE,
  resolveFreeBookingMode,
  resolveChargeableVenueSubtotal,
  resolveChargeableVenueSubtotalFromBooking
} from './bookingFreeMode';

export const parseMoney = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const normalized = String(value).trim().toUpperCase();
  if (normalized === 'TBC' || normalized === 'N/A') return null;
  const parsed = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

export const roundMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const sumPackageLines = (booking, req) => {
  const fromBooking = Array.isArray(booking?.packages) ? booking.packages : null;
  const pkgs =
    fromBooking && fromBooking.length > 0 ? fromBooking : req?.packages;
  if (!Array.isArray(pkgs) || pkgs.length === 0) return 0;
  return roundMoney(
    pkgs.reduce((sum, line) => {
      const raw = line?.total ?? line?.amount;
      const amount = parseMoney(raw);
      return sum + (amount ?? 0);
    }, 0)
  );
};

const amountsRoughlyEqual = (a, b, tolerance = 0.02) =>
  a != null && b != null && Math.abs(Number(a) - Number(b)) < tolerance;

/** Compute space fee + venue total (space + add-ons) from stored booking fields. */
export const resolveBookingSpaceAndVenueTotals = (booking) => {
  if (!booking || typeof booking !== 'object') {
    return { baseAmount: 0, addOnsTotal: 0, venueTotal: 0 };
  }

  const req = parseBookingRequirements(booking);
  const addOnsTotal = resolveBookingAddOnsTotal(booking);
  const meta = readPricingMetadata(booking, req);
  const duration =
    parseMoney(booking.duration_hours ?? req.duration_hours) ??
    (meta.duration_hours != null ? meta.duration_hours : null);
  const computedSpace = computeBaseFromPricingMetadata({
    pricingMode: meta.pricingMode,
    headcount: meta.headcount,
    duration,
    perPersonRate: meta.perPersonRate,
    spaceHourlyRate: meta.spaceHourlyRate
  });

  const storedBaseFee = parseMoney(req.base_fee);
  const bookingAmount = parseMoney(req.booking_amount);

  let baseAmount = storedBaseFee ?? computedSpace ?? null;
  let venueTotal = null;

  if (bookingAmount != null) {
    const includesAddOns =
      addOnsTotal > 0 &&
      computedSpace != null &&
      amountsRoughlyEqual(bookingAmount, roundMoney(computedSpace + addOnsTotal));
    const spaceOnlyStored =
      addOnsTotal > 0 &&
      computedSpace != null &&
      amountsRoughlyEqual(bookingAmount, computedSpace);

    if (includesAddOns) {
      venueTotal = bookingAmount;
      if (baseAmount == null) {
        baseAmount = roundMoney(bookingAmount - addOnsTotal);
      }
    } else if (spaceOnlyStored) {
      baseAmount = baseAmount ?? bookingAmount;
      venueTotal = roundMoney(Number(bookingAmount) + addOnsTotal);
    } else if (baseAmount != null) {
      venueTotal = roundMoney(Number(baseAmount) + addOnsTotal);
    } else {
      baseAmount =
        addOnsTotal > 0 ? roundMoney(bookingAmount - addOnsTotal) : bookingAmount;
      venueTotal = bookingAmount;
    }
  }

  if (baseAmount == null && computedSpace != null) {
    baseAmount = computedSpace;
  }
  if (venueTotal == null && baseAmount != null) {
    venueTotal = roundMoney(Number(baseAmount) + addOnsTotal);
  }
  if (baseAmount == null && venueTotal != null) {
    baseAmount =
      addOnsTotal > 0 ? roundMoney(venueTotal - addOnsTotal) : venueTotal;
  }

  return {
    baseAmount: baseAmount ?? 0,
    addOnsTotal,
    venueTotal: venueTotal ?? 0
  };
};

/** Add-ons total from booking row or requirements JSON. */
export const resolveBookingAddOnsTotal = (booking) => {
  if (!booking || typeof booking !== 'object') return 0;
  const req = parseBookingRequirements(booking);
  const fromField = parseMoney(
    booking.add_ons_total ?? booking.addOnsTotal ?? req.add_ons_total
  );
  if (fromField != null && fromField > 0) return fromField;
  const fromPackages = sumPackageLines(booking, req);
  return fromPackages > 0 ? fromPackages : 0;
};

const isPaidPaymentStatus = (status) => {
  const key = String(status || '').toLowerCase();
  return key === 'paid' || key === 'completed' || key === '1';
};

export const resolveBookingAmountPaid = (booking) => {
  if (!booking || typeof booking !== 'object') return 0;
  const req = parseBookingRequirements(booking);
  if (isPaidPaymentStatus(booking.payment_status ?? booking.paymentStatus)) {
    const synced = parseMoney(booking.amount_paid ?? req.amount_paid);
    if (synced != null && synced > 0) return synced;
    const { max } = storedTotals(booking, req);
    if (max != null && max > 0) return max;
    return synced ?? 0;
  }
  return parseMoney(booking.amount_paid ?? req.amount_paid) ?? 0;
};

const storedTotals = (booking, req) => {
  const values = [
    parseMoney(booking?.final_amount ?? booking?.finalAmount),
    parseMoney(booking?.total_amount ?? booking?.totalAmount ?? booking?.total),
    parseMoney(booking?.totalPrice ?? booking?.total_price),
    parseMoney(req?.total_price ?? req?.totalPrice)
  ].filter((value) => value != null);
  if (values.length === 0) return { min: null, max: null };
  return { min: Math.min(...values), max: Math.max(...values) };
};

/** Venue hire (space fee) excluding add-ons and card/platform fees. */
export const resolveBookingBaseAmount = (booking) =>
  resolveBookingSpaceAndVenueTotals(booking).baseAmount;

/** Space + add-ons before platform/processing fees. */
export const resolveBookingVenueTotal = (booking) =>
  resolveBookingSpaceAndVenueTotals(booking).venueTotal;

/** Charge total persisted on the row (what the organiser pays). */
const resolvePersistedBookingChargeTotal = (booking) => {
  if (!booking || typeof booking !== 'object') return null;
  const snapshot = extractFeeBreakdownSnapshot(booking);
  const fromSnapshot = parseMoney(snapshot?.total_payable);
  if (fromSnapshot != null) return fromSnapshot;
  return parseMoney(booking.final_amount ?? booking.finalAmount);
};

const feeBreakdownFromPersistedSnapshot = (snapshot, fallbackParts) => {
  const venueTotal = roundMoney(
    Number(snapshot.space_fee ?? 0) + Number(snapshot.add_ons ?? 0) - Number(snapshot.discount ?? 0)
  );
  return {
    baseAmount: snapshot.space_fee ?? fallbackParts.baseAmount,
    addOnsTotal: snapshot.add_ons ?? fallbackParts.addOnsTotal,
    venueTotal: venueTotal > 0 ? venueTotal : fallbackParts.venueTotal,
    platformFee: snapshot.platform_fee ?? 0,
    processingFee: snapshot.processing_fee ?? 0,
    chargeTotal: snapshot.total_payable ?? fallbackParts.chargeTotal
  };
};

/**
 * Full fee breakdown: venue total, platform fee, processing fee, and charge total.
 * Aligns with checkout PaymentBreakdown and organiser-facing totals.
 */
export const resolveBookingFeeBreakdown = (booking, { venueId } = {}) => {
  const empty = {
    baseAmount: 0,
    addOnsTotal: 0,
    venueTotal: 0,
    platformFee: 0,
    processingFee: 0,
    chargeTotal: 0
  };
  if (!booking || typeof booking !== 'object') return empty;

  const addOnsTotal = resolveBookingAddOnsTotal(booking);
  const baseAmount = resolveBookingBaseAmount(booking);
  const venueTotal = resolveBookingVenueTotal(booking);
  const fallbackParts = { baseAmount, addOnsTotal, venueTotal, chargeTotal: 0 };

  if (isVenueBookingListSummary(booking)) {
    const persistedCharge = resolvePersistedBookingChargeTotal(booking);
    if (persistedCharge != null) {
      const snapshot = extractFeeBreakdownSnapshot(booking);
      if (snapshot?.total_payable != null) {
        return feeBreakdownFromPersistedSnapshot(snapshot, {
          ...fallbackParts,
          chargeTotal: persistedCharge
        });
      }
      const platformFee =
        parseMoney(booking.platform_fee ?? booking.platformFee) ?? 0;
      const processingFee =
        parseMoney(booking.processing_fee ?? booking.processingFee) ?? 0;
      return {
        baseAmount,
        addOnsTotal,
        venueTotal,
        platformFee,
        processingFee,
        chargeTotal: persistedCharge
      };
    }
  }

  const mode = resolveFreeBookingMode(booking);
  const chargeableVenue =
    mode === FREE_BOOKING_MODE.NONE
      ? venueTotal
      : resolveChargeableVenueSubtotalFromBooking(booking, mode);
  if (chargeableVenue <= 0) {
    return {
      ...empty,
      baseAmount,
      addOnsTotal,
      venueTotal,
      chargeTotal: 0
    };
  }

  const computed = computePaymentFeeBreakdown(chargeableVenue, {
    isPlatformBooking: !isManualBookingSource(booking),
    venueId: venueId ?? booking.venue_id ?? booking.venueId
  });
  return {
    baseAmount,
    addOnsTotal,
    venueTotal,
    platformFee: computed.platformFee,
    processingFee: computed.processingFee,
    chargeTotal: computed.chargeTotal
  };
};

/** Total payable (venue total + platform + processing). */
export const resolveBookingGrandTotal = (booking, options = {}) =>
  resolveBookingFeeBreakdown(booking, options).chargeTotal;

/** Outstanding balance (charge total minus paid). */
export const resolveBookingAmountPending = (booking, options = {}) => {
  if (!booking || typeof booking !== 'object') return 0;
  if (isPaidPaymentStatus(booking.payment_status ?? booking.paymentStatus)) {
    return 0;
  }
  const paid = resolveBookingAmountPaid(booking);
  const chargeTotal = resolveBookingGrandTotal(booking, options);
  return Math.max(0, roundMoney(chargeTotal - paid));
};

/** Organizer-facing pending balance — same as resolveBookingAmountPending. */
export const resolveDrawerOrganizerPending = (booking, { venueId } = {}) =>
  resolveBookingAmountPending(booking, { venueId });

/**
 * Shared payable breakdown for venue dashboard and organiser profile.
 * Pass `draftPricing` while the venue drawer has unsaved edits.
 */
export const resolveBookingPayableBreakdown = (booking, { venueId, draftPricing } = {}) => {
  if (draftPricing && draftPricing.useDraft) {
    const mode = draftPricing.freeBookingMode ?? FREE_BOOKING_MODE.NONE;
    const baseAmount = roundMoney(Number(draftPricing.baseAmount ?? 0));
    const addOnsTotal = roundMoney(Number(draftPricing.addOnsTotal ?? 0));
    const venueTotal = roundMoney(Number(draftPricing.venueTotal ?? 0));
    const discountAmount = roundMoney(Number(draftPricing.discountAmount ?? 0));
    const chargeableVenue = resolveChargeableVenueSubtotal({
      baseAmount,
      addOnsTotal,
      discountAmount,
      mode
    });
    const paid = roundMoney(Number(draftPricing.amountPaid ?? resolveBookingAmountPaid(booking)));

    if (chargeableVenue <= 0) {
      return {
        baseAmount,
        addOnsTotal,
        venueTotal,
        platformFee: 0,
        processingFee: 0,
        grandTotal: 0,
        amountPaid: paid,
        amountPending: 0
      };
    }

    const computed = computePaymentFeeBreakdown(chargeableVenue, {
      isPlatformBooking: !isManualBookingSource(booking),
      venueId: venueId ?? booking?.venue_id ?? booking?.venueId
    });
    const chargeTotal = computed.chargeTotal;
    return {
      baseAmount,
      addOnsTotal,
      venueTotal,
      platformFee: computed.platformFee,
      processingFee: computed.processingFee,
      grandTotal: chargeTotal,
      amountPaid: paid,
      amountPending: Math.max(0, roundMoney(chargeTotal - paid))
    };
  }
  return resolveBookingAmounts(booking, { venueId });
};

export const resolveBookingAmounts = (booking, options = {}) => {
  const fees = resolveBookingFeeBreakdown(booking, options);
  const amountPaid = resolveBookingAmountPaid(booking);
  const amountPending = resolveBookingAmountPending(booking, options);
  return {
    baseAmount: fees.baseAmount,
    addOnsTotal: fees.addOnsTotal,
    venueTotal: fees.venueTotal,
    platformFee: fees.platformFee,
    processingFee: fees.processingFee,
    grandTotal: fees.chargeTotal,
    amountPaid,
    amountPending
  };
};

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (trimmed) return trimmed;
  }
  return '';
};

/** Prefer Stripe top-level fields once payment_status is paid. */
export const resolveBookingPaymentDisplayFields = (booking) => {
  if (!booking || typeof booking !== 'object') {
    return { transactionId: '', paymentMethod: '' };
  }
  const req = parseBookingRequirements(booking);
  const paid = isPaidPaymentStatus(booking.payment_status ?? booking.paymentStatus);

  if (paid) {
    return {
      transactionId: firstNonEmptyString(
        booking.transaction_id,
        booking.transactionId,
        req.transaction_id
      ),
      paymentMethod: firstNonEmptyString(
        booking.payment_method,
        booking.paymentMethod,
        req.payment_method
      )
    };
  }

  return {
    transactionId: firstNonEmptyString(
      req.transaction_id,
      booking.transaction_id,
      booking.transactionId
    ),
    paymentMethod: firstNonEmptyString(
      req.payment_method,
      booking.payment_method,
      booking.paymentMethod
    )
  };
};
